import { useState, useRef } from "react";
import {
  ArrowLeft,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useMutation } from "@apollo/client/react";
import {
  SEND_USER_OTP,
  CREATE_USER,
  SEND_USER_LOGIN_OTP,
  SOCIAL_LOGIN_USER,
  LOGIN_USER,
} from "../../graphql/auth.js";
import { setAuthToken, setRefreshToken } from "../../graphql/client.js";
import { GoogleLogin } from "@react-oauth/google";
import { showErrorToast } from "@/lib/errorToast";
import { useTranslation } from "@/lib/i18n.jsx";
import { RecaptchaWidget } from "./RecaptchaWidget.jsx";

export const AuthForm = ({ onBack, onLogin, isNewUser }) => {
  const { t } = useTranslation();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.trim() === "") {
      return { isValid: false, error: t('auth.error.email_required', 'Email is required') };
    }
    if (!emailRegex.test(email)) {
      return { isValid: false, error: t('auth.error.email_invalid', 'Please enter a valid email address') };
    }
    if (email.length > 255) {
      return { isValid: false, error: t('auth.error.email_too_long', 'Email must be less than 255 characters') };
    }
    return { isValid: true, error: "" };
  };

  const validatePassword = (password) => {
    if (!password || password.trim() === "") {
      return { isValid: false, error: t('auth.error.password_required', 'Password is required') };
    }
    if (password.length < 8) {
      return { isValid: false, error: t('auth.error.password_too_short', 'Password must be at least 8 characters') };
    }
    if (!/[A-Z]/.test(password)) {
      return {
        isValid: false,
        error: t('auth.error.password_needs_uppercase', 'Password must contain at least one uppercase letter'),
      };
    }
    if (!/[a-z]/.test(password)) {
      return {
        isValid: false,
        error: t('auth.error.password_needs_lowercase', 'Password must contain at least one lowercase letter'),
      };
    }
    if (!/[0-9]/.test(password)) {
      return {
        isValid: false,
        error: t('auth.error.password_needs_number', 'Password must contain at least one number'),
      };
    }
    if (!/[!@#$%^&*]/.test(password)) {
      return {
        isValid: false,
        error: t('auth.error.password_needs_special', 'Password must contain at least one special character (!@#$%^&*)'),
      };
    }
    return { isValid: true, error: "" };
  };

  const validateName = (name) => {
    if (!name || name.trim() === "") {
      return { isValid: false, error: t('auth.error.name_required', 'Name is required') };
    }
    if (name.trim().length < 2) {
      return { isValid: false, error: t('auth.error.name_too_short', 'Name must be at least 2 characters') };
    }
    if (name.length > 100) {
      return { isValid: false, error: t('auth.error.name_too_long', 'Name must be less than 100 characters') };
    }
    if (!/^[a-zA-Z\s'-]+$/.test(name)) {
      return {
        isValid: false,
        error: t('auth.error.name_invalid_chars', 'Name can only contain letters, spaces, hyphens and apostrophes'),
      };
    }
    return { isValid: true, error: "" };
  };

  const [isLoginMode, setIsLoginMode] = useState(!isNewUser);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [showMessage, setshowMessage] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [twoFactorMethod, setTwoFactorMethod] = useState("EMAIL");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // OTP resend cooldown (security: prevent spam)
  const [resendCooldown, setResendCooldown] = useState(0);
  const [canResend, setCanResend] = useState(false);

  // Validation states
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    otp: "",
    form: "",
  });
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false,
    name: false,
  });

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  // Form-level (not per-field) failures - the ones a partner integrating
  // this widget would actually want surfaced prominently. Kept as the
  // existing inline banner AND toasted (routed to the parent page when
  // embedded there, per lib/errorToast.js), rather than replacing the
  // banner outright.
  const setFormError = (msg) => {
    setErrors((prev) => ({ ...prev, form: msg }));
    if (msg) showErrorToast(msg);
  };

  const validateField = (field) => {
    let validation = { isValid: true, error: "" };

    switch (field) {
      case "email":
        validation = validateEmail(email);
        break;
      case "password":
        validation = validatePassword(password);
        break;
      case "confirmPassword":
        if (!isLoginMode) {
          if (confirmPassword !== password) {
            validation = { isValid: false, error: t('auth.error.passwords_dont_match', 'Passwords do not match') };
          }
        }
        break;
      case "name":
        if (!isLoginMode) {
          validation = validateName(name);
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: validation.error }));
    return validation.isValid;
  };

  const validateForm = () => {
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);

    let nameValid = { isValid: true, error: "" };
    let confirmValid = { isValid: true, error: "" };

    if (!isLoginMode) {
      nameValid = validateName(name);
      if (confirmPassword !== password) {
        confirmValid = { isValid: false, error: t('auth.error.passwords_dont_match', 'Passwords do not match') };
      }
    }

    setErrors({
      email: emailValid.error,
      password: passwordValid.error,
      confirmPassword: confirmValid.error,
      name: nameValid.error,
      form: "",
    });

    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      name: true,
    });

    return (
      emailValid.isValid &&
      passwordValid.isValid &&
      nameValid.isValid &&
      confirmValid.isValid
    );
  };

  const [sendUserOtp] = useMutation(SEND_USER_OTP);
  const [createUser] = useMutation(CREATE_USER);
  const [loginSendOtp] = useMutation(SEND_USER_LOGIN_OTP);
  const [loginVerifyOtp] = useMutation(LOGIN_USER);

  // Start resend cooldown timer
  const startResendCooldown = () => {
    setCanResend(false);
    setResendCooldown(60); // 60 seconds cooldown

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Validate OTP format
  const validateOtp = (otpValue) => {
    if (!otpValue || otpValue.trim() === "") {
      return { isValid: false, error: t('auth.error.otp_required', 'OTP is required') };
    }
    if (!/^\d{6}$/.test(otpValue)) {
      return { isValid: false, error: t('auth.error.otp_invalid_length', 'OTP must be exactly 6 digits') };
    }
    return { isValid: true, error: "" };
  };

  // Create user --> Send otp on Mail
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If OTP already sent, verify it
    if (otpSent && !isLoginMode) {
      verifyOtp();
      return;
    }

    if (otpSent && isLoginMode) {
      verifyLoginOtp();
      return;
    }

    // Login mode - send OTP
    if (isLoginMode) {
      sendOtpinmail(e);
      return;
    }

    // Signup mode - validate and send OTP
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors((prev) => ({ ...prev, form: "" }));

      // API CALL: Send OTP for signup
      const res = await sendUserOtp({
        variables: {
          email: email.trim().toLowerCase(), // Normalize email
          recaptchaToken,
        },
      });

      if (res.data.sendUserOtp.success) {
        setshowMessage(
          res.data.sendUserOtp.message || t('auth.message.otp_sent', 'OTP sent to your email'),
        );
        setOtpSent(true);
        startResendCooldown(); // Start cooldown timer

        // Clear success message after 5 seconds
        setTimeout(() => {
          setshowMessage("");
        }, 5000);
      } else {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        setFormError(
          res.data.sendUserOtp.message ||
          t('auth.error.send_otp_failed', 'Failed to send OTP. Please try again.'),
        );
      }
    } catch (err) {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      // Security: Don't expose internal error details
      setFormError(t('auth.error.send_otp_network', 'Unable to send OTP. Please check your connection and try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Create user --> Verify OTP and Create user
  const verifyOtp = async () => {
    // Validate OTP format
    const otpValidation = validateOtp(otp);
    if (!otpValidation.isValid) {
      setErrors((prev) => ({
        ...prev,
        otp: otpValidation.error,
        form: "",
      }));
      return;
    }

    try {
      setLoading(true);
      setErrors((prev) => ({ ...prev, otp: "", form: "" }));

      // API CALL: Create user with OTP verification
      const res = await createUser({
        variables: {
          data: {
            email: email.trim().toLowerCase(),
            password,
            name: name.trim(),
            otp: otp.trim(),
          },
        },
      });

      const response = res.data?.createUser;

      if (!response?.success) {
        setFormError(response?.message || t('auth.error.registration_failed', 'Registration failed. Please try again.'));
        return;
      }

      // Security: Store auth token if returned
      const accessToken = response?.data?.accessToken || response?.data?.token;
      if (accessToken) { setAuthToken(accessToken); setRefreshToken(response.data.refreshToken); }

      // Success - proceed to next step
      onLogin(email.trim().toLowerCase(), false);
    } catch (err) {
      // Security: Don't expose internal error details
      setFormError(t('auth.error.verification_failed', 'Verification failed. Please check OTP and try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Login user --> Send OTP to email
  const sendOtpinmail = async (e) => {
    e.preventDefault();

    // Validate email and password before sending
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);

    if (!emailValid.isValid || !passwordValid.isValid) {
      setErrors((prev) => ({
        ...prev,
        email: emailValid.error,
        password: passwordValid.error,
      }));
      setTouched({
        email: true,
        password: true,
        confirmPassword: false,
        name: false,
      });
      return;
    }

    try {
      setLoading(true);
      setErrors((prev) => ({ ...prev, form: "" }));

      // API CALL: Send login OTP
      const res = await loginSendOtp({
        variables: {
          email: email.trim().toLowerCase(),
          password: password,
          recaptchaToken,
        },
      });

      const response = res.data?.loginSendOtp;

      if (response?.success) {
        const method = response.data?.method === 'TOTP' ? 'TOTP' : 'EMAIL';
        setTwoFactorMethod(method);
        setshowMessage(response.message || t('auth.message.otp_sent', 'OTP sent to your email'));
        setOtpSent(true);
        if (method !== 'TOTP') startResendCooldown(); // No resend concept for an authenticator code

        // Clear success message after 5 seconds
        setTimeout(() => {
          setshowMessage("");
        }, 5000);
      } else {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        // Security: Generic error for invalid credentials
        setFormError(response?.message || t('auth.error.invalid_credentials', 'Invalid email or password. Please try again.'));
      }
    } catch (err) {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      // Security: Don't expose internal error details
      setFormError(t('auth.error.login_process_failed', 'Unable to process login. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  // Login user --> Verify OTP and Login user
  const verifyLoginOtp = async () => {
    // Validate OTP format
    const otpValidation = validateOtp(otp);
    if (!otpValidation.isValid) {
      setErrors((prev) => ({
        ...prev,
        otp: otpValidation.error,
        form: "",
      }));
      return;
    }

    try {
      setLoading(true);
      setErrors((prev) => ({ ...prev, otp: "", form: "" }));

      // API CALL: Verify login OTP
      const res = await loginVerifyOtp({
        variables: {
          data: {
            email: email.trim().toLowerCase(),
            otp: otp.trim(),
          },
        },
      });

      const response = res.data?.loginVerifyOtp;

      if (!response?.success) {
        setFormError(response?.message || t('auth.error.invalid_otp', 'Invalid OTP. Please try again.'));
        return;
      }

      // Security: Store auth token if returned
      const accessToken = response?.data?.accessToken || response?.data?.token;
      if (accessToken) { setAuthToken(accessToken); setRefreshToken(response.data.refreshToken); }

      // Success - determine if existing user (with KYC) or new
      const isExistingUser = response?.data?.isKycComplete ?? false;
      onLogin(email.trim().toLowerCase(), isExistingUser, response?.data);
    } catch (err) {
      // Security: Don't expose internal error details
      setFormError(t('auth.error.login_failed', 'Login failed. Please check OTP and try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    if (!canResend) return;

    setOtp(""); // Clear current OTP
    setErrors((prev) => ({ ...prev, otp: "", form: "" }));

    if (isLoginMode) {
      // Resend login OTP
      await sendOtpinmail({ preventDefault: () => { } });
    } else {
      // Resend signup OTP
      try {
        setLoading(true);
        const res = await sendUserOtp({
          variables: { email: email.trim().toLowerCase(), recaptchaToken },
        });

        if (res.data?.sendUserOtp?.success) {
          setshowMessage(t('auth.message.otp_resent', 'OTP resent successfully'));
          startResendCooldown();
          setTimeout(() => setshowMessage(""), 5000);
        } else {
          setFormError(res.data?.sendUserOtp?.message || t('auth.error.resend_otp_failed', 'Failed to resend OTP'));
        }
      } catch (err) {
        setFormError(t('auth.error.resend_otp_network', 'Unable to resend OTP. Please try again.'));
      } finally {
        setLoading(false);
      }
    }
  };

  const [socialLoginUser] = useMutation(SOCIAL_LOGIN_USER);

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setSocialLoading("google");

      const token = credentialResponse.credential;

      // ✅ Decode user (base64url → base64 before atob)
      const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const user = JSON.parse(atob(b64));

      // 🔥 CALL YOUR API
      const res = await socialLoginUser({
        variables: {
          data: {
            idToken: token,        // ✅ JWT token (NOT access_token)
            email: user.email,
            name: user.name,
          },
        },
      });

      const response = res.data?.socialLoginUser;

      if (!response?.success) {
        setFormError(response?.message || t('auth.error.google_login_failed', 'Google login failed'));
        return;
      }

      // ✅ Store tokens
      const accessToken = response?.data?.accessToken || response?.data?.token;
      if (accessToken) { setAuthToken(accessToken); setRefreshToken(response.data.refreshToken); }

      // ✅ Login success — isKycComplete tells the widget if KYC is already done
      const isKycComplete = response?.data?.isKycComplete ?? false;
      onLogin(user.email, isKycComplete, response?.data);

    } catch (error) {
      console.error(error);
      setFormError(t('auth.error.google_login_failed', 'Google login failed'));
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="animate-fade-in p-6 ">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2.5 bg-secondary border border-border hover:bg-muted transition-all rounded-xl"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-bold">
            {isLoginMode ? t('auth.header.welcome_back', 'Welcome Back') : t('auth.header.create_account', 'Create Account')}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isLoginMode
              ? t('auth.subheader.sign_in_to_trade', 'Sign in to continue your trade')
              : t('auth.subheader.sign_up_to_trade', 'Sign up to start trading')}
          </p>
        </div>
      </div>

      {/* Social Login Buttons */}
      <div className="w-full flex justify-center items-center space-y-6 mb-6">
        <div className="mx-auto">
          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => setFormError(t('auth.error.google_login_failed', 'Google login failed'))}
            width={"100%"}
          />

          {/* <button
          onClick={() => handleSocialLogin("apple")}
          disabled={loading || socialLoading}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-foreground text-background rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {socialLoading === "apple" ? (
            <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
          )}
          <span className="font-semibold text-sm">Continue with Apple</span>
        </button> */}
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">
          {t('auth.divider.or_continue_with_email', 'or continue with email')}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name field (signup only) */}
        {!isLoginMode && (
          <div className="space-y-2 animate-slide-up">
            <label className="section-label">{t('auth.label.full_name', 'Full Name')}</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleBlur("name")}
                placeholder={t('auth.placeholder.full_name', 'Enter your full name')}
                className={`input-field pl-12 ${touched.name && errors.name ? "border-destructive" : ""}`}
              />
            </div>
            {touched.name && errors.name && (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </div>
            )}
          </div>
        )}

        {/* Email field */}
        <div
          className="space-y-2 animate-slide-up"
          style={{ animationDelay: "0.05s" }}
        >
          <label className="section-label">{t('auth.label.email_address', 'Email Address')}</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="email"
              value={email}
              autoComplete="false"
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur("email")}
              placeholder={t('auth.placeholder.email', 'Enter your email')}
              className={`input-field pl-12 ${touched.email && errors.email ? "border-destructive" : ""}`}
            />
          </div>
          {touched.email && errors.email && (
            <div className="flex items-center gap-2 text-destructive text-sm font-medium">
              <AlertCircle className="w-4 h-4" />
              {errors.email}
            </div>
          )}
        </div>

        {/* Password field */}
        <div
          className="space-y-2 animate-slide-up"
          style={{ animationDelay: "0.1s" }}
        >
          <label className="section-label">{t('auth.label.password', 'Password')}</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur("password")}
              placeholder={t('auth.placeholder.password', 'Enter your password')}
              autoComplete="new-password"
              className={`input-field pl-12 pr-12 ${touched.password && errors.password ? "border-destructive" : ""}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {touched.password && errors.password && (
            <div className="flex items-center gap-2 text-destructive text-sm font-medium">
              <AlertCircle className="w-4 h-4" />
              {errors.password}
            </div>
          )}
        </div>

        {/* Confirm Password (signup only) */}
        {!isLoginMode && (
          <div
            className="space-y-2 animate-slide-up"
            style={{ animationDelay: "0.15s" }}
          >
            <label className="section-label">{t('auth.label.confirm_password', 'Confirm Password')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => handleBlur("confirmPassword")}
                placeholder={t('auth.placeholder.confirm_password', 'Confirm your password')}
                className={`input-field pl-12 ${touched.confirmPassword && errors.confirmPassword ? "border-destructive" : ""}`}
              />
            </div>
            {touched.confirmPassword && errors.confirmPassword && (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                {errors.confirmPassword}
              </div>
            )}
          </div>
        )}

        {/* Form error message */}
        {errors.form && (
          <div className="p-3 bg-destructive/15 border border-destructive/40 text-destructive text-sm font-medium animate-scale-in rounded-md">
            {errors.form}
          </div>
        )}

        {/* Form success message */}
        {showMessage && (
          <div className="p-3 bg-primary/15 border border-primary/40 text-primary text-sm font-medium animate-scale-in rounded-md">
            {showMessage}
          </div>
        )}

        {otpSent && (
          <div className="space-y-2 animate-slide-up">
            <div className="flex items-center justify-between">
              <label className="section-label">
                {twoFactorMethod === 'TOTP'
                  ? t('auth.label.enter_totp', 'Enter Authenticator Code')
                  : t('auth.label.enter_otp', 'Enter OTP')}
              </label>
              {twoFactorMethod !== 'TOTP' && (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={!canResend || loading}
                  className={`text-xs font-medium flex items-center gap-1 transition-colors ${canResend && !loading
                    ? "text-accent hover:text-accent/80 cursor-pointer"
                    : "text-muted-foreground cursor-not-allowed"
                    }`}
                >
                  <RefreshCw
                    className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}
                  />
                  {resendCooldown > 0
                    ? t('auth.otp.resend_in_seconds', 'Resend in {seconds}s').replace('{seconds}', resendCooldown)
                    : t('auth.otp.resend_otp', 'Resend OTP')}
                </button>
              )}
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={otp}
              onChange={(e) => {
                // Only allow numeric input
                const value = e.target.value.replace(/\D/g, "");
                setOtp(value);
                if (errors.otp) {
                  setErrors((prev) => ({ ...prev, otp: "" }));
                }
              }}
              placeholder={twoFactorMethod === 'TOTP'
                ? t('auth.placeholder.totp', 'Enter 6-digit code')
                : t('auth.placeholder.otp', 'Enter 6-digit OTP')}
              maxLength={6}
              className={`input-field text-center tracking-widest font-bold ${errors.otp ? "border-destructive" : ""}`}
              autoComplete="one-time-code"
            />
            {errors.otp && (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                {errors.otp}
              </div>
            )}
            <p className="text-xs text-muted-foreground text-center">
              {twoFactorMethod === 'TOTP'
                ? t('auth.otp.from_authenticator_app', 'Enter the code from your authenticator app')
                : t('auth.otp.sent_to_email', 'OTP sent to {email}').replace('{email}', email)}
            </p>
          </div>
        )}

        {!otpSent && (
          <RecaptchaWidget ref={recaptchaRef} onChange={setRecaptchaToken} />
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading || socialLoading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
              <span>{t('auth.submit.please_wait', 'Please wait...')}</span>
            </>
          ) : isLoginMode ? (
            t('auth.submit.sign_in', 'Sign In')
          ) : (
            t('auth.submit.create_account', 'Create Account')
          )}
        </button>
      </form>

      {/* Toggle login/signup */}
      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          {isLoginMode ? t('auth.toggle.no_account', "Don't have an account?") : t('auth.toggle.have_account', 'Already have an account?')}
          <button
            onClick={() => {
              setIsLoginMode(!isLoginMode);
              setTwoFactorMethod("EMAIL");
              setErrors({
                email: "",
                password: "",
                confirmPassword: "",
                name: "",
                form: "",
              });
              setTouched({
                email: false,
                password: false,
                confirmPassword: false,
                name: false,
              });
            }}
            className="ml-2 text-accent font-bold hover:underline"
          >
            {isLoginMode ? t('auth.toggle.sign_up', 'Sign Up') : t('auth.toggle.sign_in', 'Sign In')}
          </button>
        </p>
      </div>
    </div>
  );
};
